/**
 * _parking-types.ts — local types for the Parking module (V0.3 marquee).
 *
 * Kept in the routes/ directory (filename prefixed with `_` so it's plainly
 * a helper not a routable page) so we don't churn @hooddesk/shared-types from
 * the frontend agent's seat. Once the backend agent's shared-types PR lands
 * we can swap these imports for the canonical exports.
 *
 * Contracts are LOCKED with the parallel API agent. See the spec block in
 * routes/parking.tsx for endpoint shapes.
 */

// ───── enums ─────

export type ParkingSlotOwnership =
  | 'society_owned'
  | 'unit_owned'
  | 'builder_unsold'
  | 'common_visitor';

export type ParkingSlotType =
  | 'covered'
  | 'open'
  | 'two_wheeler'
  | 'ev_charging'
  | 'handicapped'
  | 'visitor';

export type ParkingSlotStatus = 'available' | 'allocated' | 'reserved' | 'blocked';

export type VehicleSizeClass = 'two_wheeler' | 'hatchback' | 'sedan' | 'suv' | 'oversized';

export type AllocationKind =
  | 'owner_self_use'
  | 'society_rental'
  | 'owner_rental'
  | 'guest_temp';

export type ChargerOwnership = 'society_installed' | 'owner_installed';
export type ChargerLocation = 'in_parking_slot' | 'common_area';
export type ChargerStatus = 'active' | 'broken' | 'maintenance' | 'retired';
export type ConnectorType = 'type1' | 'type2' | 'ccs2' | 'chademo' | 'gbt' | 'three_pin';

export type SocietyParkingModel = 'all_purchased' | 'all_society_rental' | 'hybrid';

export type DisputeStatus = 'open' | 'resolved' | 'invalid';
export type MarketplaceListingStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'taken';

export type ChargerRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type EvSessionStatus = 'active' | 'ended' | 'paid';
export type VisitorSessionStatus = 'active' | 'ended';

// ───── core records ─────

export interface ParkingZone {
  id: string;
  society_id?: string;
  name: string;
  code: string;
  tower_id?: string | null;
  total_slots: number;
  default_ownership_kind: ParkingSlotOwnership;
  default_slot_type: ParkingSlotType;
  default_vehicle_size: VehicleSizeClass;
  default_monthly_fee_inr?: number | null;
  notes?: string | null;
  is_active: boolean;
  // Optional aggregates the server may include for cheap KPI strips.
  _count?: { slots: number };
  occupied_count?: number;
  available_count?: number;
}

export interface ParkingSlot {
  id: string;
  society_id?: string;
  zone_id: string;
  slot_number: string;
  slot_type: ParkingSlotType;
  vehicle_size: VehicleSizeClass;
  ownership_kind: ParkingSlotOwnership;
  owner_unit_id?: string | null;
  purchase_price_inr?: number | null;
  sale_deed_ref?: string | null;
  status: ParkingSlotStatus;
  reserved_for_special_needs: boolean;
  monthly_fee_inr?: number | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  zone?: { id: string; name: string; code: string } | null;
  owner_unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
  active_allocation?: ParkingAllocation | null;
}

export interface ParkingAllocation {
  id: string;
  society_id?: string;
  slot_id: string;
  using_unit_id: string;
  using_vehicle_id?: string | null;
  allocation_kind: AllocationKind;
  monthly_rent_inr?: number | null;
  rent_payable_to_kind?: string | null;
  rent_payable_to_unit_id?: string | null;
  society_cut_pct?: number | null;
  rental_start?: string | null;
  rental_end?: string | null;
  is_primary_slot: boolean;
  allocated_at: string;
  ended_at?: string | null;
  end_reason?: string | null;
  slot?: { id: string; slot_number: string; zone?: { id: string; name: string } | null } | null;
  using_unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
  using_vehicle?: { id: string; number_plate: string } | null;
}

export interface ParkingDispute {
  id: string;
  society_id?: string;
  slot_id?: string | null;
  raised_by_user_id?: string | null;
  raised_by_name?: string | null;
  raised_by_unit_id?: string | null;
  description: string;
  photo_ref?: string | null;
  status: DisputeStatus;
  resolution_notes?: string | null;
  resolved_at?: string | null;
  created_at: string;
  slot?: { id: string; slot_number: string; zone?: { id: string; name: string } | null } | null;
  raised_by_unit?: { id: string; number: string } | null;
}

export interface VisitorParkingSession {
  id: string;
  society_id?: string;
  slot_id: string;
  visitor_log_id?: string | null;
  vehicle_plate: string;
  visitor_name?: string | null;
  visiting_unit_id?: string | null;
  parked_at: string;
  expected_until?: string | null;
  ended_at?: string | null;
  guard_id?: string | null;
  status: VisitorSessionStatus;
  notes?: string | null;
  slot?: { id: string; slot_number: string; zone?: { id: string; name: string } | null } | null;
  visiting_unit?: { id: string; number: string } | null;
  guard?: { id: string; full_name: string } | null;
}

export interface MarketplaceListing {
  id: string;
  society_id?: string;
  slot_id: string;
  listed_by_unit_id: string;
  monthly_rent_inr: number;
  notice_period_days: number;
  status: MarketplaceListingStatus;
  notes?: string | null;
  created_at: string;
  approved_at?: string | null;
  approved_allocation_id?: string | null;
  slot?: { id: string; slot_number: string; zone?: { id: string; name: string } | null } | null;
  listed_by_unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
}

// ───── marketplace enquiry (resident applies for owner-listed slot) ─────
export type MarketplaceEnquiryStatus = 'pending' | 'matched' | 'declined' | 'cancelled';

export interface MarketplaceEnquiry {
  id: string;
  society_id?: string;
  listing_id: string;
  enquirer_unit_id: string;
  enquirer_user_id: string | null;
  vehicle_id: string | null;
  notes: string | null;
  status: MarketplaceEnquiryStatus;
  decided_at: string | null;
  decided_by_user_id: string | null;
  decision_notes: string | null;
  created_at: string;
  updated_at: string;
}

export const ENQUIRY_STATUS_LABEL: Record<MarketplaceEnquiryStatus, string> = {
  pending: 'Pending',
  matched: 'Matched',
  declined: 'Declined',
  cancelled: 'Cancelled'
};

export const ENQUIRY_STATUS_BADGE: Record<MarketplaceEnquiryStatus, 'info' | 'ok' | 'alert' | 'muted'> = {
  pending: 'info',
  matched: 'ok',
  declined: 'alert',
  cancelled: 'muted'
};

export interface EvCharger {
  id: string;
  society_id?: string;
  zone_id?: string | null;
  slot_id?: string | null;
  location_kind: ChargerLocation;
  location_label?: string | null;
  ownership_kind: ChargerOwnership;
  owner_unit_id?: string | null;
  brand?: string | null;
  model?: string | null;
  power_kw: number;
  connector_type: ConnectorType;
  kwh_rate_inr?: number | null;
  status: ChargerStatus;
  photo_ref?: string | null;
  serial_no?: string | null;
  installed_at?: string | null;
  notes?: string | null;
  zone?: { id: string; name: string } | null;
  slot?: { id: string; slot_number: string } | null;
  owner_unit?: { id: string; number: string } | null;
  // Cheap aggregates server may include.
  total_kwh?: number;
  total_revenue_inr?: number;
  total_sessions?: number;
}

export interface ChargerInstallRequest {
  id: string;
  society_id?: string;
  requesting_unit_id: string;
  slot_id?: string | null;
  brand?: string | null;
  power_kw?: number | null;
  connector_type?: ConnectorType | null;
  proposed_date?: string | null;
  notes?: string | null;
  status: ChargerRequestStatus;
  decision_notes?: string | null;
  decided_at?: string | null;
  created_at: string;
  requesting_unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
  slot?: { id: string; slot_number: string } | null;
}

export interface EvSession {
  id: string;
  society_id?: string;
  charger_id: string;
  using_unit_id?: string | null;
  using_user_id?: string | null;
  using_vehicle_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  kwh_consumed?: number | null;
  amount_inr?: number | null;
  kwh_rate_inr?: number | null;
  status: EvSessionStatus;
  paid_at?: string | null;
  payment_ref?: string | null;
  notes?: string | null;
  charger?: { id: string; location_label?: string | null } | null;
  using_unit?: { id: string; number: string } | null;
  using_vehicle?: { id: string; number_plate: string } | null;
}

// ───── society parking policy (subset of Society fields) ─────

export interface SocietyParkingPolicy {
  parking_model: SocietyParkingModel;
  parking_first_slot_in_maintenance: boolean;
  parking_allow_owner_rental: boolean;
  parking_society_cut_pct: number;
  parking_rental_notice_days: number;
  parking_max_slots_per_unit: number;
  parking_two_wheeler_no_charge: boolean;
  parking_visitor_max_hours: number;
  parking_ev_default_kwh_rate_inr: number;
}

// ───── label / variant maps (UI-only) ─────

export const SLOT_TYPE_LABEL: Record<ParkingSlotType, string> = {
  covered: 'Covered',
  open: 'Open',
  two_wheeler: 'Two-wheeler',
  ev_charging: 'EV charging',
  handicapped: 'Handicapped',
  visitor: 'Visitor'
};

export const VEHICLE_SIZE_LABEL: Record<VehicleSizeClass, string> = {
  two_wheeler: 'Two-wheeler',
  hatchback: 'Hatchback',
  sedan: 'Sedan',
  suv: 'SUV',
  oversized: 'Oversized'
};

export const OWNERSHIP_LABEL: Record<ParkingSlotOwnership, string> = {
  society_owned: 'Society',
  unit_owned: 'Unit-owned',
  builder_unsold: 'Builder (unsold)',
  common_visitor: 'Visitor / common'
};

export const STATUS_LABEL: Record<ParkingSlotStatus, string> = {
  available: 'Available',
  allocated: 'Allocated',
  reserved: 'Reserved',
  blocked: 'Blocked'
};

export const STATUS_BADGE: Record<ParkingSlotStatus, 'ok' | 'info' | 'warn' | 'muted'> = {
  available: 'ok',
  allocated: 'info',
  reserved: 'warn',
  blocked: 'muted'
};

export const ALLOCATION_KIND_LABEL: Record<AllocationKind, string> = {
  owner_self_use: 'Owner self-use',
  society_rental: 'Society rental',
  owner_rental: 'Owner rental',
  guest_temp: 'Guest / temporary'
};

export const PARKING_MODEL_LABEL: Record<SocietyParkingModel, string> = {
  all_purchased: 'All purchased',
  all_society_rental: 'All society rental',
  hybrid: 'Hybrid'
};

export const CHARGER_STATUS_LABEL: Record<ChargerStatus, string> = {
  active: 'Active',
  broken: 'Broken',
  maintenance: 'Maintenance',
  retired: 'Retired'
};

export const CHARGER_STATUS_BADGE: Record<ChargerStatus, 'ok' | 'alert' | 'warn' | 'muted'> = {
  active: 'ok',
  broken: 'alert',
  maintenance: 'warn',
  retired: 'muted'
};

export const REQUEST_STATUS_BADGE: Record<ChargerRequestStatus, 'info' | 'ok' | 'alert' | 'muted'> = {
  pending: 'info',
  approved: 'ok',
  rejected: 'alert',
  cancelled: 'muted'
};

export const DISPUTE_STATUS_BADGE: Record<DisputeStatus, 'alert' | 'ok' | 'muted'> = {
  open: 'alert',
  resolved: 'ok',
  invalid: 'muted'
};

export const MARKETPLACE_STATUS_BADGE: Record<MarketplaceListingStatus, 'info' | 'warn' | 'ok' | 'alert' | 'muted'> = {
  pending: 'warn',
  approved: 'ok',
  rejected: 'alert',
  cancelled: 'muted',
  taken: 'info'
};

// Cursor list-response generic shape used by all parking GETs.
export interface CursorList<T> {
  items: T[];
  next_cursor?: string | null;
}

// ───── lightweight lookups used by forms (units / blocks etc.) ─────

export interface UnitLite {
  id: string;
  number: string;
  block?: { id: string; name: string } | null;
}
export interface BlockLite {
  id: string;
  name: string;
}
